// Editable resume draft and its versions. A draft is copied out of the master
// profile, never a live view of it: editing a draft must never touch the stored
// profile. Every copied fact keeps the `state` it had in the profile so a resume
// can always show which parts a human has confirmed and which are still only
// suggestions.

import type { EntryBase, FactState } from '../profile/types';

/** Sections a resume can show. Contact details are always rendered in the
 *  header, so only the reorderable body sections are listed here. */
export type ResumeSectionId =
  | 'summary'
  | 'experience'
  | 'education'
  | 'skills'
  | 'certifications'
  | 'projects';

export const RESUME_SECTIONS: readonly ResumeSectionId[] = [
  'summary',
  'experience',
  'education',
  'skills',
  'certifications',
  'projects',
];

export const RESUME_SECTION_LABEL: Record<ResumeSectionId, string> = {
  summary: 'Summary',
  experience: 'Experience',
  education: 'Education',
  skills: 'Skills',
  certifications: 'Certifications',
  projects: 'Projects',
};

export type TemplateId = 'classic' | 'modern' | 'compact';

export const TEMPLATE_IDS: readonly TemplateId[] = ['classic', 'modern', 'compact'];

export const TEMPLATE_LABEL: Record<TemplateId, string> = {
  classic: 'Classic',
  modern: 'Modern',
  compact: 'Compact',
};

export const TEMPLATE_DESCRIPTION: Record<TemplateId, string> = {
  classic: 'Single column, serif headings, generous spacing. The safest choice for any ATS.',
  modern: 'Single column with sans-serif headings and an accent rule under each section.',
  compact: 'Single column with tight spacing, for resumes that run long.',
};

/** Contact details copied from the profile, each keeping its provenance. */
export type ResumeContactField = {
  value: string;
  state: FactState;
};

export type ResumeContact = {
  fullName: ResumeContactField;
  email: ResumeContactField;
  phone: ResumeContactField;
  location: ResumeContactField;
  links: Array<{ id: string; label: string; url: string; state: FactState }>;
};

/** A resume entry copies the profile entry's content and provenance, and tracks
 *  whether the draft text differs from the profile it came from. */
export type ResumeEntryBase = EntryBase & {
  /** True once the draft text has been edited away from the profile copy. */
  edited: boolean;
};

export type ResumeExperienceEntry = ResumeEntryBase & {
  organization: string;
  role: string;
  location: string;
  periodRaw: string;
  responsibilities: string[];
  achievements: string[];
};

export type ResumeEducationEntry = ResumeEntryBase & {
  institution: string;
  credential: string;
  area: string;
  periodRaw: string;
};

export type ResumeSkillEntry = ResumeEntryBase & {
  name: string;
};

export type ResumeCertificationEntry = ResumeEntryBase & {
  name: string;
  issuer: string;
  date: string;
};

export type ResumeProjectEntry = ResumeEntryBase & {
  name: string;
  url: string;
  description: string;
  periodRaw: string;
};

export type ResumeEntries = {
  experience: ResumeExperienceEntry;
  education: ResumeEducationEntry;
  skills: ResumeSkillEntry;
  certifications: ResumeCertificationEntry;
  projects: ResumeProjectEntry;
};

export type ResumeEntrySection = keyof ResumeEntries;

export const RESUME_ENTRY_SECTIONS: readonly ResumeEntrySection[] = [
  'experience',
  'education',
  'skills',
  'certifications',
  'projects',
];

export type AnyResumeEntry = ResumeEntries[ResumeEntrySection];

/** Section order the user has chosen for this draft. Removing a section takes it
 *  out of this list only; the entries stay in the draft so it can be restored. */
export type ResumeDraft = {
  id: string;
  /** Human label for the draft, e.g. the role it targets. */
  name: string;
  targetRole: string;
  contact: ResumeContact;
  summary: ResumeContactField;
  /** Body section order. A section omitted here is hidden, not deleted. */
  sectionOrder: ResumeSectionId[];
  experience: ResumeExperienceEntry[];
  education: ResumeEducationEntry[];
  skills: ResumeSkillEntry[];
  certifications: ResumeCertificationEntry[];
  projects: ResumeProjectEntry[];
  template: TemplateId;
  /** ISO timestamp of the last edit, for version labels. */
  updatedAt: string;
};

/** Immutable snapshot of a draft, kept in memory for compare and restore. */
export type ResumeVersion = {
  id: string;
  /** User-facing name, unique within a session. */
  name: string;
  /** ISO timestamp the version was saved. */
  savedAt: string;
  template: TemplateId;
  /** Deep copy of the draft at save time. */
  snapshot: ResumeDraft;
};

export type ResumeDiffKind = 'added' | 'removed' | 'changed';

export type ResumeDiffLine = {
  kind: ResumeDiffKind;
  /** Section the line belongs to, or 'meta' for non-section fields. */
  section: 'meta' | ResumeSectionId;
  label: string;
  before: string;
  after: string;
};

export type ResumeComparison = {
  fromVersionId: string;
  toVersionId: string;
  lines: ResumeDiffLine[];
  identical: boolean;
};
